import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import "@/App.css";

// Public Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import FlightRequestForm from "@/pages/FlightRequestForm";
import VisaRequestPage from "@/pages/VisaRequestPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ContactPage from "@/pages/ContactPage";

// Agent Pages
import AgentLogin from "@/pages/AgentLogin";
import Dashboard from "@/pages/Dashboard";
import FlightRequests from "@/pages/FlightRequests";
import RequestDetail from "@/pages/RequestDetail";
import Bookings from "@/pages/Bookings";
import BookingDetail from "@/pages/BookingDetail";
import TicketGenerator from "@/pages/TicketGenerator";
import AccountingPage from "./pages/AccountingPage";
import QuickExpense from "./pages/QuickExpense";
import PaymentResult from "./pages/PaymentResult";
import Quotes from "./pages/Quotes";
import Contacts from "./pages/Contacts";
import VisaRequests from "./pages/VisaRequests";
import VisaQuotesList from "./pages/VisaQuotesList";
import VisaQuoteGenerator from "./pages/VisaQuoteGenerator";
import InvoiceList from "./pages/InvoiceList";
import InvoiceDetail from "./pages/InvoiceDetail";
import ReceiptList from "./pages/ReceiptList";
import ReceiptDetail from "./pages/ReceiptDetail";
import PaymentSettings from "./pages/PaymentSettings";
import PaymentsDue from "./pages/PaymentsDue";
import WorkingHours from "./pages/WorkingHours";
import DocumentArchive from "./pages/DocumentArchive";
import HotelBookings from "./pages/HotelBookings";
import HotelBookingDetail from "./pages/HotelBookingDetail";
import HotelRequestForm from "./pages/HotelRequestForm";
import HotelRequests from "./pages/HotelRequests";
import UmrahPackages from "./pages/UmrahPackages";
import UmrahPackageDetail from "./pages/UmrahPackageDetail";
import UmrahQuotations from "./pages/UmrahQuotations";
import UmrahBookings from "./pages/UmrahBookings";
import UmrahBookingDetail from "./pages/UmrahBookingDetail";
import ProvisionalItineraryList from "./pages/ProvisionalItineraryList";
import ProvisionalItineraryDetail from "./pages/ProvisionalItineraryDetail";
import AgentActivity from "./pages/AgentActivity";
import EmployeeList from "./pages/EmployeeList";
import EmployeeDetail from "./pages/EmployeeDetail";

// Auth Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/flight-request" element={<FlightRequestForm />} />
      <Route path="/visa-request" element={<VisaRequestPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<AgentLogin />} />
      <Route path="/accounting" element={<ProtectedRoute><AccountingPage /></ProtectedRoute>} />
  <Route path="/expenses/quick" element={<ProtectedRoute><QuickExpense /></ProtectedRoute>} />
  <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
  <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
  <Route path="/visa-requests" element={<ProtectedRoute><VisaRequests /></ProtectedRoute>} />
  <Route path="/visa-quotes" element={<ProtectedRoute><VisaQuotesList /></ProtectedRoute>} />
  <Route path="/visa-requests/:requestId/quote" element={<ProtectedRoute><VisaQuoteGenerator /></ProtectedRoute>} />
  <Route path="/invoices" element={<ProtectedRoute><InvoiceList /></ProtectedRoute>} />
  <Route path="/invoices/:invoiceId" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
  <Route path="/receipts" element={<ProtectedRoute><ReceiptList /></ProtectedRoute>} />
  <Route path="/receipts/:receiptId" element={<ProtectedRoute><ReceiptDetail /></ProtectedRoute>} />
  <Route path="/settings/payment" element={<ProtectedRoute><PaymentSettings /></ProtectedRoute>} />
  <Route path="/payments-due" element={<ProtectedRoute><PaymentsDue /></ProtectedRoute>} />
  <Route path="/working-hours" element={<ProtectedRoute><WorkingHours /></ProtectedRoute>} />
  <Route path="/document-archive" element={<ProtectedRoute><DocumentArchive /></ProtectedRoute>} />
  <Route path="/hotel-bookings" element={<ProtectedRoute><HotelBookings /></ProtectedRoute>} />
  <Route path="/hotel-bookings/:id" element={<ProtectedRoute><HotelBookingDetail /></ProtectedRoute>} />
  <Route path="/hotel-request" element={<HotelRequestForm />} />
  <Route path="/hotel-requests" element={<ProtectedRoute><HotelRequests /></ProtectedRoute>} />
  <Route path="/umrah-packages" element={<ProtectedRoute><UmrahPackages /></ProtectedRoute>} />
  <Route path="/umrah-packages/:id" element={<ProtectedRoute><UmrahPackageDetail /></ProtectedRoute>} />
  <Route path="/umrah-quotations" element={<ProtectedRoute><UmrahQuotations /></ProtectedRoute>} />
  <Route path="/umrah-bookings" element={<ProtectedRoute><UmrahBookings /></ProtectedRoute>} />
  <Route path="/umrah-bookings/:id" element={<ProtectedRoute><UmrahBookingDetail /></ProtectedRoute>} />
  <Route path="/provisional-itineraries" element={<ProtectedRoute><ProvisionalItineraryList /></ProtectedRoute>} />
  <Route path="/provisional-itineraries/:piId" element={<ProtectedRoute><ProvisionalItineraryDetail /></ProtectedRoute>} />
  <Route path="/agent-activity" element={<ProtectedRoute><AgentActivity /></ProtectedRoute>} />
  <Route path="/employees" element={<ProtectedRoute><EmployeeList /></ProtectedRoute>} />
  <Route path="/employees/:id" element={<ProtectedRoute><EmployeeDetail /></ProtectedRoute>} />

<Route path="/payment-result/:bookingId" element={<PaymentResult />} />
      {/* Protected Agent Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/requests" element={
        <ProtectedRoute><FlightRequests /></ProtectedRoute>
      } />
      <Route path="/requests/:id" element={
        <ProtectedRoute><RequestDetail /></ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute><Bookings /></ProtectedRoute>
      } />
      <Route path="/bookings/:id" element={
        <ProtectedRoute><BookingDetail /></ProtectedRoute>
      } />
      <Route path="/tickets/:bookingId" element={
        <ProtectedRoute><TicketGenerator /></ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
